"use client";

import { useState } from "react";
import CustomLink from "@components/atoms/Link";
import { FAQItem } from "@components/molecules/FAQItem";
import Text from "@components/atoms/Text";
import { EventType, trackEventWithCommonProps } from "@utils/eventTracker";
import { FAQ } from "@components/molecules/FAQItem/FAQItem.types";
import styles from "./FAQSection.module.scss";

const INITIAL_FAQ_COUNT = 5;
const FAQ_PAGE_SIZE = 5;

interface FAQSectionClientProps {
  faqs: FAQ[];
  slug?: string;
  cityName?: string;
  brandName?: string;
  modelName?: string;
  variantName?: string;
  carAvailability?: string;
  pageType?: string;
  hideShowAll?: boolean;
}

export default function FAQSectionClient({
  faqs,
  slug,
  cityName,
  brandName,
  modelName,
  variantName,
  carAvailability,
  pageType,
  hideShowAll,
}: FAQSectionClientProps) {
  const [visibleCount, setVisibleCount] = useState(INITIAL_FAQ_COUNT);
  const hasMoreFaqs = visibleCount < faqs.length;
  const redirectUrl = slug ? `/cars/${slug}/faqs/` : "faqs/";
  const trackingProperties = {
    car_availability: carAvailability,
    city_name: cityName,
    vehicle_make: brandName,
    vehicle_model: modelName,
    ...(variantName && { vehicle_variant: variantName }),
  };

  const handleShowMore = () => {
    setVisibleCount((currentCount) =>
      Math.min(currentCount + FAQ_PAGE_SIZE, faqs.length)
    );
    void trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
      pageType,
      additionalProps: {
        action_type: "tap_btn",
        action_name: "tap_btn_show_more_faq",
        ...trackingProperties,
      },
    });
  };

  return (
    <section aria-labelledby="faq-title" className={styles.section}>
      <div className={styles.wrapper}>
        <div className={styles.leftCol}>
          <Text level="h2" className={styles.heading}>
            Frequently <br className={styles.desktopOnlyBreak} />
            asked questions
          </Text>
        </div>

        <div className={styles.rightCol}>
          {faqs.slice(0, visibleCount).map((faq, index) => (
            <FAQItem key={index} faq={faq} />
          ))}

          {!hideShowAll && (
            <div className={styles.linkWrapper}>
              {hasMoreFaqs ? (
                <button
                  type="button"
                  className={styles.showAllLink}
                  onClick={handleShowMore}
                >
                  Show more
                </button>
              ) : (
                <CustomLink
                  size="ds"
                  href={redirectUrl}
                  className={styles.showAllLink}
                  segmentEventProps={{
                    eventName: "action",
                    pageType,
                    eventProperties: {
                      action_type: "tap_btn",
                      action_name: "tap_btn_all_faq",
                      ...trackingProperties,
                    },
                  }}
                >
                  See all FAQs
                </CustomLink>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
